import { useEffect, useRef, useState } from "react";
import DeviceMetricApi from "../../../../../../api/DeviceMetricApi";
import { ChartHeader, ChartHeaderColorBlock, ChartInfo, ChartName, ChartUnit, ChartWrapper, ChartsWrapper, EChartsWrapper, FilterBar, FilterItem, FilterItemLabel, HistoryDeviceMetricsWrapper, SearchResult } from "./HistoryDeviceMetrics.styled";
import { Button, DatePicker, Segmented, Select, Space, Table } from "antd";
import { ExportOutlined, LineChartOutlined, SearchOutlined, TableOutlined } from "@ant-design/icons";

import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import * as echarts from "echarts";

import StringUtils from "../../../../../../util/StringUtils";
import SeriesColorUtils from "../../../../../../util/SeriesColorUtils";
import ViewStateService from "../../../../../../services/view-state/ViewStateService";
import TimeUtils from "../../../../../../util/TimeUtils";
import { useSearchParams } from "react-router-dom";

interface HistoryDeviceMetricsProps {
    deviceView: any
}

const HistoryDeviceMetrics: React.FC<HistoryDeviceMetricsProps> = (props)=> {
    // params
    let [searchParams] = useSearchParams();
    let defaultStartTime = searchParams.get('startTime') ? dayjs(parseInt(searchParams.get('startTime'))) : dayjs().add(-1, 'd');
    let defaultEndTime = searchParams.get('endTime') ? dayjs(parseInt(searchParams.get('endTime'))) : dayjs();
    let autoQuery = searchParams.get('autoQuery') == 'true';

    let deviceView = props.deviceView;
    let deviceTemplateConfiguration = JSON.parse(deviceView.deviceTemplate.configuration);
    let deviceTemplateProperties = deviceTemplateConfiguration.properties;
    let propertyOptions = deviceTemplateProperties.map((e)=> {
        return {
            label: e.name,
            value: e.key,
        }
    });
    propertyOptions.splice(0, 0, {label: '全部', value: 'ALL'});

    // api
    let deviceMetricApi = new DeviceMetricApi();

    // reference
    let firstRenderRef = useRef(true);
    let echartsDomsRef = useRef<HTMLElement[]>([]);
    let echartsRef =useRef<echarts.ECharts[]>([]);

    // state
    let [targetedPropertyKeys, setTargetedPropertyKeys] = useState(propertyOptions.map((e)=> e.value));
    let [startTime, setStartTime] = useState(defaultStartTime);
    let [endTime, setEndTime] = useState(defaultEndTime);
    let [aggregateType, setAggregateType] = useState('RAW');
    let [timeScale, setTimeScale] = useState('RAW');
    let [displayType, setDisplayType] = useState('CHARTS');
    let [loading, setLoading] = useState(false);

    // metrics
    let [deviceMetricsSeriesList, setDeviceMetricsSeriesList] = useState([]);
    let [deviceMetricsTableData, setDeviceMetricsTableData] = useState([]);
    let deviceMetricsSeriesProperties = deviceMetricsSeriesList.map(e=> e.property);

    let onTargetedPropertiesChange = (selectedValues: string[])=> {
        let newHasAll = selectedValues.indexOf('ALL') >= 0;
        let hasAll= targetedPropertyKeys.indexOf('ALL') >= 0;
        
        let newSelectedProperties = [].concat(selectedValues);
        if (newHasAll && hasAll) {
            if(selectedValues.length < targetedPropertyKeys.length) {
                // item unselected
                newSelectedProperties = newSelectedProperties.filter((e)=> e != 'ALL');
            }
        } else if(newHasAll) {
            // select all
            for(let deviceTemplateProperty of deviceTemplateProperties) {
                if(newSelectedProperties.indexOf(deviceTemplateProperty.key) < 0) {
                    newSelectedProperties.push(deviceTemplateProperty.key);
                }
            }
        } else if(hasAll) {
            // has unselected items
            newSelectedProperties = newSelectedProperties.filter((e)=> e!= 'ALL');
        } else if (selectedValues.length == propertyOptions.length) {
            // all selected
            newSelectedProperties.push('ALL');
        }
        setTargetedPropertyKeys(newSelectedProperties);
    };

    let onDateTimeRangeChange = (dateTimeRangeValue: any[])=> {
        setStartTime(dateTimeRangeValue[0]);
        setEndTime(dateTimeRangeValue[1]);
    };

    let onAggregateTypeChanged = (selectedValue: string)=> {
        setAggregateType(selectedValue);
    };

    let onTimeScaleChanged = (selectedValue: string)=> {
        setTimeScale(selectedValue);
    };

    let onDisplayTypeChange = (selectedValue: string)=> {
        setDisplayType(selectedValue);
    };

    let onQueryButtonClick = async ()=> {
        setLoading(true);

        // process start/end time
        let startTimestamp = null;
        if (startTime!=null) {
            startTimestamp = dayjs(startTime).toDate().getTime();
        }
        let endTimestamp = null;
        if (endTime!=null) {
            endTimestamp = dayjs(endTime).toDate().getTime();
        }

        // fetch data
        let deviceMetricsSeriesList = (await deviceMetricApi.queryHistoricalDeviceMetricsSeries({
            deviceId: deviceView.device.id,
            properties: targetedPropertyKeys,
            startTime: startTimestamp,
            endTime: endTimestamp,
            aggregateType: aggregateType,
            timeScale: timeScale,
            autoTimescale: displayType=='TABLE' ? false : true,
        })).data.data;

        // table data
        let deviceMetricsTableData = [];
        let timestamp2RowData = {};
        for(let deviceMetricsSeries of deviceMetricsSeriesList) {
            let property = deviceMetricsSeries.property;
            let propertyKey = property.key;
            for(let metric of deviceMetricsSeries.metrics) {
                let rowData = timestamp2RowData['' + metric.timestamp];
                if (rowData==null) {
                    rowData = {timestamp: Number(metric.timestamp)};
                    timestamp2RowData['' + metric.timestamp] = rowData;
                    deviceMetricsTableData.push(rowData);
                }
                
                // filter error metrics (mainly caused by sensor errors)
                let errorMetric = false;
                if (property.min != null && Number(metric.value) < property.min) {
                    errorMetric = true;
                } else if (property.max != null && Number(metric.value) > property.max) {
                    errorMetric = true;
                }
                if (errorMetric) {
                    rowData[propertyKey] = '故障';
                } else {
                    rowData[propertyKey] = metric.value;
                }
            }
        }
        deviceMetricsTableData = deviceMetricsTableData.sort((a, b)=> {
            return a.timestamp - b.timestamp;
        });

        // update data
        setDeviceMetricsSeriesList(deviceMetricsSeriesList);
        setDeviceMetricsTableData(deviceMetricsTableData);

        // clear loding
        setLoading(false);
    };

    let onExportButtonClick = async ()=> {
        // process start/end time
        let startTimestamp = null;
        if (startTime!=null) {
            startTimestamp = dayjs(startTime).toDate().getTime();
        }
        let endTimestamp = null;
        if (endTime!=null) {
            endTimestamp = dayjs(endTime).toDate().getTime();
        }

        // fetch data
        let deviceMetricsExportTask = (await deviceMetricApi.exportHistoricalDeviceMetrics({
            deviceId: deviceView.device.id,
            properties: targetedPropertyKeys,
            startTime: startTimestamp,
            endTime: endTimestamp,
            aggregateType: aggregateType,
            timeScale: timeScale,
        })).data.data;

        // fire new task event
        ViewStateService.getInstance().fireNewTaskListeners();
    };

    // init ECharts
    let formatTimestampAxis = (value)=> {
        let text = moment(value).format('YYYY/MM/DD HH:mm:ss');
        text=text.replace(' ', '\n');
        return text;
    }

    let getTimescaleByTimeRange=(timeRange: number)=> {
        if (timeRange < TimeUtils.hoursToMillis(2)) {
            return TimescaleType.TS_RAW;
        } else if (timeRange < TimeUtils.daysToMillis(2)) {
            return TimescaleType.TS_1MIN;
        } else if(timeRange < TimeUtils.daysToMillis(10)) {
            return TimescaleType.TS_5MIN;
        } else if(timeRange < TimeUtils.daysToMillis(60)) {
            return TimescaleType.TS_30MIN;
        } else if(timeRange < TimeUtils.daysToMillis(120)) {
            return TimescaleType.TS_1HOUR;
        } else {
            return TimescaleType.TS_1DAY;
        }
    }

    let getTimecalePeriod=(timescaleType: TimescaleType)=> {
        switch(timescaleType) {
            case TimescaleType.TS_RAW: 
                return 0;
            case TimescaleType.TS_1MIN: 
                return TimeUtils.minutesToMillis(1);
            case TimescaleType.TS_5MIN: 
                return TimeUtils.minutesToMillis(5);
            case TimescaleType.TS_30MIN: 
                return TimeUtils.minutesToMillis(30);
            case TimescaleType.TS_1HOUR: 
                return TimeUtils.hoursToMillis(1);
            case TimescaleType.TS_1DAY: 
                return TimeUtils.daysToMillis(1);
        }
        throw 'Unknown timescale type: ' + timescaleType;
    }

    let initECharts = (property: any, dom: HTMLElement)=> {
        let propertyIndex = deviceMetricsSeriesProperties.map(e=>e.key).indexOf(property.key);

        // dispose on unload
        if (dom == null) {
            // element unmount event
            let echarts = echartsRef.current[propertyIndex];
            echarts.dispose();
            return;
        }

        // already initialized
        if (echarts.getInstanceByDom(dom)) {
            return;
        }

        // save to reference
        echartsDomsRef.current[propertyIndex]=dom;

        // init echarts
        let chart = echarts.init(dom);
        echartsRef.current[propertyIndex]=chart;

        // series
        let deviceMetricsSeries = deviceMetricsSeriesList.filter(e=>e.property.key==property.key)[0];
        let seriesData = [];
        let metrics = deviceMetricsSeries.metrics;
        let minTime = dayjs(startTime).toDate().getTime();
        let maxTime = dayjs(endTime).toDate().getTime();
        let timescaleType = getTimescaleByTimeRange(maxTime-minTime);
        let timescalePeriod = getTimecalePeriod(timescaleType);
        let paddingGapMillis = timescalePeriod <= 0 ? TimeUtils.minutesToMillis(1) : timescalePeriod * 2;
        let lastMetricTimestamp: number = null;
        for (let i = 0; i < metrics.length; i++) {
            let metric = metrics[i];

            // filter error metrics (mainly caused by sensor errors)
            let errorMetric = false;
            if (property.min != null && Number(metric.value) < property.min) {
                errorMetric = true;
            } else if (property.max != null && Number(metric.value) > property.max) {
                errorMetric = true;
            }
            if (errorMetric) {
                continue;
            }
            
            // pad data point
            if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                // down data point
                // let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), null]};
                // seriesData.push(downDataPoint);

                // up data point
                // let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), null]};
                // seriesData.push(upDataPoint);
                seriesData.push(null);
            }
            lastMetricTimestamp = metric.timestamp;

            // add to series data
            seriesData.push({
                name: metric.timestamp + '',
                value: [Number(metric.timestamp), Number(metric.value)],
            });
        }

        // init options
        let option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    animation: false
                },
                backgroundColor: "rgba(0,0,0,0.7)",
                confine: true,
                formatter: function(params) {
                    let seriesContent = '';
                    for(let param of params) {
                        if (param.value == null) {
                            continue;
                        }
                        let errorText = '';
                        if (property.min != null && Number(param.value[1]) < property.min) {
                            errorText = '（故障）';
                        } else if (property.max != null && Number(param.value[1]) > property.max) {
                            errorText = '（故障）';
                        }

                        seriesContent += `
                            <div style="color: #EEEEEE;">
                                <div style="margin: 0px 0px 5px 0px;">
                                    <div style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 5px; background-color: ${param.color}"></div>
                                    <div style="display: inline-block">${param.seriesName}</div>
                                </div>
                                <div style="padding: 5px 0px;">
                                    <span style="display: inline-block; width: 50px;">测量值:</span> 
                                    <span style="display: inline-block">${param.value[1]} ${StringUtils.nonull(property.unit)} ${errorText}</span>
                                </div>
                                <div>
                                    <span style="display: inline-block; width: 50px;">时间:</span> 
                                    <span style="display: inline-block">${moment(param.value[0]).format('YYYY-MM-DD HH:mm:ss')}</span>
                                </div>
                            </div>
                        `;
                        break;
                    }
                    let content=`
                        <div style="width: 220px;">
                            ${seriesContent}
                        </div>
                    `;
                    return content;
                },
            },
            grid: {
                x: 50,
                y: 20,
                x2: 20,
                y2: 40,
            },
            xAxis: {
                type: 'time',
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
                splitNumber: 2,
                axisLabel: {
                    show: true,
                    formatter: function (value, index) {
                        return formatTimestampAxis(value);
                    }
                },
                min: minTime,
                max: maxTime,
            },
            yAxis: {
                type: 'value',
                boundaryGap: [0, '100%'],
                axisLine: {
                    show: true
                },
                minorTick: {
                    show: true
                },
                minorSplitLine: {
                    show: true
                },
                splitLine: {
                    show: true,
                },
                nameTextStyle: {
                    align: 'left',
                },
                min: property.min != null ? parseFloat(property.min) : 'dataMin',
                max: property.max != null ? parseFloat(property.max) : 'dataMax',
            },
            dataZoom: [
                {
                  type: 'inside',
                  start: 0,
                  end: 100
                }
            ],
            series: [
                {
                    name: property.name,
                    type: 'line',
                    color: SeriesColorUtils.getSeriesColor(propertyIndex),
                    smooth: false,
                    showSymbol: false,
                    areaStyle: {
                        opacity: 0.2,
                    },
                    data: seriesData,
                    shadowBlur: 20,
                }
            ],
        };

        chart.setOption(option);
        chart.group = 'DeviceHistoryMetrics';
        echarts.connect('DeviceHistoryMetrics');

        let refreshingTimescale = false;
        let lastQueryStartTime: number = minTime;
        let lastQueryEndTime: number = maxTime;

        let onDataZoom = async (e)=> {
            if(refreshingTimescale) {
                return;
            }
            if(propertyIndex!=0) {
                return;
            }
            if(e.batch) {
                e=e.batch[0];
            }
            let startPercent = e.start;
            let endPercent = e.end;

            let startTime = Math.round(minTime+(maxTime-minTime) * startPercent / 100.0);
            let endTime = Math.round(minTime+(maxTime-minTime) * endPercent / 100.0);
            let rangeTime = endTime - startTime;
            let queryStartTime = Math.max(minTime, Math.round(startTime - rangeTime * 1/2));
            let queryEndTime = Math.min(maxTime, Math.round(endTime + rangeTime * 1/2));
            let queryRangeTime = queryEndTime - queryStartTime;
            let nextTimescaleType = getTimescaleByTimeRange(queryRangeTime);
            if (timescaleType != nextTimescaleType || !(lastQueryStartTime <= startTime && endTime <= lastQueryEndTime)) {
                // fetch data
                refreshingTimescale = true;
                try {
                    let deviceMetricsSeriesList = (await deviceMetricApi.queryHistoricalDeviceMetricsSeries({
                        deviceId: deviceView.device.id,
                        properties: targetedPropertyKeys,
                        startTime: queryStartTime,
                        endTime: queryEndTime,
                        aggregateType: aggregateType,
                        timeScale: timeScale,
                        autoTimescale: true,
                    })).data.data;

                    for(let i=0; i<echartsRef.current.length; i++) {
                        let property = deviceTemplateProperties[i];
                        let deviceMetricsSeries = deviceMetricsSeriesList.filter(e=>e.property.key==property.key)[0];
                        let seriesData = [];
                        let metrics = deviceMetricsSeries.metrics;
                        let timescalePeriod = getTimecalePeriod(nextTimescaleType);
                        let paddingGapMillis = timescalePeriod <= 0 ? TimeUtils.minutesToMillis(1) : timescalePeriod * 2;
                        let lastMetricTimestamp: number = null;
                        for (let i = 0; i < metrics.length; i++) {
                            let metric = metrics[i];

                            // filter error metrics (mainly caused by sensor errors)
                            let errorMetric = false;
                            if (property.min != null && Number(metric.value) < property.min) {
                                errorMetric = true;
                            } else if (property.max != null && Number(metric.value) > property.max) {
                                errorMetric = true;
                            }
                            if (errorMetric) {
                                continue;
                            }
                            
                            // pad data point
                            if (lastMetricTimestamp !=null && Math.abs(metric.timestamp - lastMetricTimestamp) > paddingGapMillis) {
                                // down data point
                                // let downDataPoint = {name: '' + (Number(lastMetricTimestamp)+1), value: [(Number(lastMetricTimestamp)+1), 0]};
                                // seriesData.push(downDataPoint);
                
                                // up data point
                                // let upDataPoint: any = {name: '' + (Number(metric.timestamp)-1), value: [(Number(metric.timestamp)-1), 0]};
                                // seriesData.push(upDataPoint);
                                seriesData.push(null);
                            }
                            lastMetricTimestamp = metric.timestamp;
                
                            // add to series data
                            seriesData.push({
                                name: metric.timestamp + '',
                                value: [Number(metric.timestamp), Number(metric.value)],
                            });
                        }

                        let chart = echartsRef.current[i];
                        chart.setOption({
                            series: [
                                {
                                    data: seriesData,
                                    shadowBlur: 20,
                                }
                            ],
                        });
                    }
                } finally {
                    refreshingTimescale = false;
                    lastQueryStartTime = queryStartTime;
                    lastQueryEndTime = queryEndTime;
                    timescaleType = nextTimescaleType;
                }
            }
        };
        chart.on('datazoom', (e)=> {
            onDataZoom(e);
        });
    }

    // effect
    useEffect(()=> {
        if (autoQuery) {
            onQueryButtonClick();
        }
    }, []);

    useEffect(()=> {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
        } else if(!queryExportDisabled()) {
            onQueryButtonClick();
        }
    }, [displayType]);

    // render
    let queryExportDisabled = ()=> {
        if (targetedPropertyKeys.filter(e=> e!=null).length > 0 && startTime!=null && endTime!=null) {
            return false;
        }
        return true;
    };

    let formatTimestamp = (timestamp)=> {
        return moment(Number(timestamp)).format('YYYY/MM/DD HH:mm:ss');
    };

    let columns: any[] = [
        {
            title: '数据时间',
            dataIndex: 'deviceMetric.timestamp',
            key: 'deviceMetric.timestamp',
            align: 'center',
            render: (text, record, index)=> {
                return formatTimestamp(record.timestamp);
            }
        },
    ];

    for(let targetedPropertyKey of targetedPropertyKeys) {
        if (targetedPropertyKey == 'ALL') {
            continue;
        }
        let targetedProperty = deviceTemplateProperties.filter(e=>e.key == targetedPropertyKey)[0];
        let column = {
            title: targetedProperty.name,
            dataIndex: targetedProperty.key,
            align: 'center',
            render: (text, record, index)=> {
                return <span>{record[targetedProperty.key]}</span>
            }
        }
        if (StringUtils.isEmpty(targetedProperty.unit)) {
            column.title = column.title + `（${StringUtils.emptyToSlash(targetedProperty.unit)}）`;
        }
        columns.push(column);
    }

    let paddingChartWrapper = [];
    for(let i=deviceMetricsSeriesList.length; i<4; i++) {
        paddingChartWrapper.push(i);
    }

    return (
        <HistoryDeviceMetricsWrapper>
            <FilterBar>
                <Space size="middle">
                    <FilterItem>
                        <FilterItemLabel>设备变量</FilterItemLabel>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount={2}
                            style={{ width: '150px' }}
                            placeholder="请选择目标变量"
                            defaultValue={targetedPropertyKeys}
                            value={targetedPropertyKeys}
                            onChange={onTargetedPropertiesChange}
                            options={propertyOptions}
                        />
                    </FilterItem>

                    <FilterItem>
                        <FilterItemLabel>时间范围</FilterItemLabel>
                        <DatePicker.RangePicker
                            style={{width: '330px'}}
                            showTime={{ format: 'HH:mm:ss' }}
                            format="YYYY-MM-DD HH:mm:ss"
                            defaultValue={[dayjs().add(-1, 'd'), dayjs()]}
                            value={[startTime, endTime]}
                            allowEmpty={[false, true]}
                            onOk={onDateTimeRangeChange}
                        />
                    </FilterItem>

                    <FilterItem>
                        <FilterItemLabel>数据聚合</FilterItemLabel>
                        <Select
                            style={{ width: '100px' }}
                            placeholder="请选择数据聚合类型"
                            defaultValue={'RAW'}
                            value={aggregateType}
                            onChange={onAggregateTypeChanged}
                            options={[
                                {label: '原始数据', value: 'RAW'},
                                {label: '最大值', value: 'MAX'},
                                {label: '最小值', value: 'MIN'},
                                {label: '最小值-最大值', value: 'MIN_MAX'},
                                {label: '平均值', value: 'AVG'},
                            ]}
                        />
                    </FilterItem>

                    <FilterItem>
                        <FilterItemLabel>时间维度</FilterItemLabel>
                        <Select
                            style={{ width: '90px' }}
                            placeholder="请选择时间维度"
                            defaultValue={'RAW'}
                            value={timeScale}
                            onChange={onTimeScaleChanged}
                            options={[
                                {label: '无', value: 'RAW'},
                                {label: '1分钟', value: '1MIN'},
                                {label: '5分钟', value: '5MIN'},
                                {label: '30分钟', value: '30MIN'},
                                {label: '1小时', value: '1HOUR'},
                                {label: '1天', value: '1DAY'},
                            ]}
                        />
                    </FilterItem>

                    <FilterItem>
                    <FilterItemLabel>展示方式</FilterItemLabel>
                        <Segmented
                            options={[
                                { label: '图表', value: 'CHARTS', icon: <LineChartOutlined /> },
                                { label: '表格', value: 'TABLE', icon: <TableOutlined /> },
                            ]}
                            value={displayType}
                            onChange={onDisplayTypeChange}
                        />
                    </FilterItem>
                </Space>

                <Space size="middle">
                    <Button type="primary" 
                        icon={<SearchOutlined style={{marginTop: '3px'}}/>} 
                        onClick={onQueryButtonClick}
                        className="query-button"
                        disabled={queryExportDisabled()}
                    >查询</Button>
                    <Button 
                        type="primary" 
                        icon={<ExportOutlined />} 
                        onClick={onExportButtonClick}
                        disabled={queryExportDisabled()}
                    >导出</Button>
                </Space>
            </FilterBar>

            {
                deviceMetricsSeriesList.length > 0 && (
                    displayType == 'TABLE' 
                    ? <SearchResult>
                            <Table
                                rowKey={(record)=> record.timestamp}
                                columns={columns} 
                                dataSource={deviceMetricsTableData}
                                loading={loading}
                            />
                    </SearchResult>

                    : <ChartsWrapper>
                    {
                        deviceMetricsSeriesProperties.map((e, index)=> {
                            return (
                                <ChartWrapper key={e.key}>
                                    <ChartHeader>
                                        <ChartName>{e.name}</ChartName>
                                        <ChartHeaderColorBlock style={{backgroundColor: SeriesColorUtils.getSeriesColor(index)}} />
                                        <ChartInfo>
                                            <ChartUnit>单位：{StringUtils.emptyToSlash(e.unit)}</ChartUnit>
                                        </ChartInfo>
                                    </ChartHeader>
                                    <EChartsWrapper ref={(dom)=> initECharts(e, dom)}>
                                    </EChartsWrapper>
                                </ChartWrapper>
                            )
                        })
                    }
                    {
                        paddingChartWrapper.map((e)=> {
                            return (
                                <ChartWrapper key={e}>
                                </ChartWrapper>
                            )
                        })
                    }
                    </ChartsWrapper>
                )
            }
        </HistoryDeviceMetricsWrapper>
    )
}

enum TimescaleType {
    TS_RAW = "RAW",
    TS_1MIN = "1MIN",
    TS_5MIN = "5MIN",
    TS_30MIN = "30MIN",
    TS_1HOUR = "1HOUR",
    TS_1DAY = "1DAY",
}

export default HistoryDeviceMetrics;

